import { JSX, useCallback, useState } from 'react';
import type { DropResult } from '@hello-pangea/dnd';
import { DragDropContext, Droppable } from '@hello-pangea/dnd';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Tooltip as HeroTooltip } from '@heroui/react';
import { ArrowUpDown, Clock, SortAsc } from 'lucide-react';

import { useConnectivity } from '~/contexts/ConnectivityContext';
import { useUIStore } from '~/stores/uiStore';

import { ErrorBoundary } from '~/components/ErrorBoundary';
import { Feedback } from '~/components/Feedback';
import { LoadingSpinner } from '~/components/LoadingSpinner';
import { RefreshButton } from '~/components/RefreshButton';
import { Tooltip } from '~/components/Tooltip';

import { TaskStatus } from '~/types';
import type { SubtaskProps, TaskProps } from '~/types';

import { ProjectsBoardColumn } from './Column';

// Define sort options as a type for better type safety
type SortOption = 'priority' | 'newest' | 'oldest';

interface ProjectsBoardProps {
  tasks: TaskProps[] | null;
  isLoading?: boolean;
  error?: Error | null;
  currentProject?: {
    description?: string | null;
    id: string;
    title: string;
  } | null;
  onTaskCreate: (status: TaskStatus) => void;
  onTaskSelect: (task: TaskProps) => void;
  onTaskUpdate: (taskId: string, updates: Partial<TaskProps>) => void;
  onTaskDelete: (taskId: string) => void;
  onSubtaskCreate: (taskId: string) => void;
  onSubtaskUpdate: (taskId: string, subtaskId: string, updates: Partial<SubtaskProps>) => void;
  onSubtaskDelete: (taskId: string, subtaskId: string) => void;
  onStatusChange: (taskId: string, status: TaskStatus) => void;
  onDragEnd: (result: DropResult) => void;
}

// Priority value mapping for sorting (HIGH > MEDIUM > LOW)
const priorityValue: Record<string, number> = {
  HIGH: 3,
  MEDIUM: 2,
  LOW: 1,
};

export function ProjectsBoard(props: ProjectsBoardProps): JSX.Element {
  const {
    currentProject,
    error,
    isLoading,
    onDragEnd,
    onStatusChange,
    onSubtaskCreate,
    onSubtaskDelete,
    onSubtaskUpdate,
    onTaskCreate,
    onTaskDelete,
    onTaskSelect,
    onTaskUpdate,
    tasks,
  } = props;
  const { isSidebarCollapsed } = useUIStore();
  const { isOnline } = useConnectivity();
  // Add state for sort option
  // Default to 'oldest' (createdAt ascending) to preserve task dependency order
  // Tasks are created in dependency order, so oldest tasks should be implemented first
  const [sortOption, setSortOption] = useState<SortOption>('oldest');

  // Determine if we should show error state vs stale data
  const hasData = tasks !== null && tasks.length > 0;
  const isStale = !!error && hasData;
  // Only show error UI if there's an error AND no data to show AND we're online
  const shouldShowError = !!error && !hasData && isOnline;

  const handleCreateTodoTask = useCallback(() => onTaskCreate(TaskStatus.TODO), [onTaskCreate]);
  const handleCreateInProgressTask = useCallback(
    () => onTaskCreate(TaskStatus.IN_PROGRESS),
    [onTaskCreate],
  );
  const handleCreateDoneTask = useCallback(() => onTaskCreate(TaskStatus.DONE), [onTaskCreate]);

  // Handler callbacks for sorting options
  const handleSortByPriority = useCallback(() => setSortOption('priority'), []);
  const handleSortByNewest = useCallback(() => setSortOption('newest'), []);
  const handleSortByOldest = useCallback(() => setSortOption('oldest'), []);

  // Sort tasks by the selected option
  const sortTasks = useCallback(
    (taskList: TaskProps[]) => {
      return [...taskList].sort((a, b) => {
        switch (sortOption) {
          case 'priority':
            return priorityValue[b.priority] - priorityValue[a.priority];
          case 'newest':
            return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
          case 'oldest':
            return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
          default:
            return 0;
        }
      });
    },
    [sortOption],
  );

  // Only show loading spinner on initial load (no data yet)
  if (isLoading && !hasData) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  // Only show error if we have NO data to display AND we're online
  // If offline or have stale data, keep showing the UI
  if (shouldShowError) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Feedback description={error.message} type="error" />
      </div>
    );
  }

  if (!tasks) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <p className="text-muted">Select a project to view tasks</p>
      </div>
    );
  }

  // Apply sorting to each task list
  const todoTasks = sortTasks(tasks.filter(task => task.status === TaskStatus.TODO));
  const inProgressTasks = sortTasks(tasks.filter(task => task.status === TaskStatus.IN_PROGRESS));
  const doneTasks = sortTasks(tasks.filter(task => task.status === TaskStatus.DONE));

  return (
    <ErrorBoundary>
      <div className="flex flex-col flex-1 h-full overflow-hidden">
        <div className="shrink-0 flex justify-between items-start gap-6 bg-surface px-6 py-4 border-b border-default">
          <div className="flex items-center gap-3">
            {isSidebarCollapsed && currentProject ? (
              <div className="max-w-5xl">
                <h2 className="text-2xl font-semibold">{currentProject.title}</h2>
                {currentProject.description && (
                  <p className="mt-1 text-sm text-muted line-clamp-2">
                    {currentProject.description}
                  </p>
                )}
              </div>
            ) : (
              <h2 className="text-2xl font-semibold">{currentProject?.title || 'Tasks'}</h2>
            )}

            {/* Stale/offline indicator */}
            {(isStale || !isOnline) && (
              <HeroTooltip
                content={
                  !isOnline
                    ? 'Connection lost. Showing cached data.'
                    : 'Unable to refresh. Showing cached data.'
                }
              >
                <div className="flex items-center gap-1 text-warning text-xs px-2 py-1 bg-warning/10 rounded">
                  <ExclamationTriangleIcon className="h-4 w-4" />
                  {!isOnline ? 'Offline' : 'Stale'}
                </div>
              </HeroTooltip>
            )}
          </div>

          <div className="flex shrink-0 items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted hidden sm:inline">Sort by:</span>
              <fieldset className="inline-flex rounded-md shadow-sm">
                <Tooltip content="Sort by priority (High → Low)" placement="bottom">
                  <button
                    type="button"
                    onClick={handleSortByPriority}
                    className={`px-3 py-1.5 text-sm font-medium ${
                      sortOption === 'priority'
                        ? 'bg-primary text-white dark:bg-primary/90 '
                        : 'bg-surface dark:bg-surface-elevated text-secondary-600 dark:text-muted hover:bg-surface-elevated '
                    } border border-default rounded-l-md focus:outline-none focus:z-10`}
                    aria-label="Sort by priority"
                  >
                    <ArrowUpDown className="h-4 w-4" />
                  </button>
                </Tooltip>

                <Tooltip content="Sort by newest first" placement="bottom">
                  <button
                    type="button"
                    onClick={handleSortByNewest}
                    className={`px-3 py-1.5 text-sm font-medium ${
                      sortOption === 'newest'
                        ? 'bg-primary text-white dark:bg-primary/90 '
                        : 'bg-surface dark:bg-surface-elevated text-secondary-600 dark:text-muted hover:bg-surface-elevated '
                    } border-t border-b border-default focus:outline-none focus:z-10`}
                    aria-label="Sort by newest first"
                  >
                    <Clock className="h-4 w-4" />
                  </button>
                </Tooltip>

                <Tooltip content="Sort by oldest first" placement="bottom">
                  <button
                    type="button"
                    onClick={handleSortByOldest}
                    className={`px-3 py-1.5 text-sm font-medium ${
                      sortOption === 'oldest'
                        ? 'bg-primary text-white dark:bg-primary/90 '
                        : 'bg-surface dark:bg-surface-elevated text-secondary-600 dark:text-muted hover:bg-surface-elevated '
                    } border border-default rounded-r-md focus:outline-none focus:z-10`}
                    aria-label="Sort by oldest first"
                  >
                    <SortAsc className="h-4 w-4" />
                  </button>
                </Tooltip>
              </fieldset>
            </div>
            <RefreshButton className="ml-auto" />
          </div>
        </div>

        <DragDropContext onDragEnd={onDragEnd}>
          <div className="flex-1 flex flex-col lg:flex-row gap-6 p-6 min-h-0 overflow-hidden">
            <div className="flex-1 lg:w-1/3 flex flex-col min-h-0">
              <Droppable droppableId={TaskStatus.TODO}>
                {provided => (
                  <ProjectsBoardColumn
                    title="To Do"
                    status={TaskStatus.TODO}
                    tasks={todoTasks}
                    onTaskAdd={handleCreateTodoTask}
                    onTaskSelect={onTaskSelect}
                    onTaskUpdate={onTaskUpdate}
                    onTaskDelete={onTaskDelete}
                    onSubtaskAdd={onSubtaskCreate}
                    onSubtaskUpdate={onSubtaskUpdate}
                    onSubtaskDelete={onSubtaskDelete}
                    onStatusChange={onStatusChange}
                    droppableProvided={provided}
                  />
                )}
              </Droppable>
            </div>

            <div className="flex-1 lg:w-1/3 flex flex-col min-h-0">
              <Droppable droppableId={TaskStatus.IN_PROGRESS}>
                {provided => (
                  <ProjectsBoardColumn
                    title="In Progress"
                    status={TaskStatus.IN_PROGRESS}
                    tasks={inProgressTasks}
                    onTaskAdd={handleCreateInProgressTask}
                    onTaskSelect={onTaskSelect}
                    onTaskUpdate={onTaskUpdate}
                    onTaskDelete={onTaskDelete}
                    onSubtaskAdd={onSubtaskCreate}
                    onSubtaskUpdate={onSubtaskUpdate}
                    onSubtaskDelete={onSubtaskDelete}
                    onStatusChange={onStatusChange}
                    droppableProvided={provided}
                  />
                )}
              </Droppable>
            </div>

            <div className="flex-1 lg:w-1/3 flex flex-col min-h-0">
              <Droppable droppableId={TaskStatus.DONE}>
                {provided => (
                  <ProjectsBoardColumn
                    title="Done"
                    status={TaskStatus.DONE}
                    tasks={doneTasks}
                    onTaskAdd={handleCreateDoneTask}
                    onTaskSelect={onTaskSelect}
                    onTaskUpdate={onTaskUpdate}
                    onTaskDelete={onTaskDelete}
                    onSubtaskAdd={onSubtaskCreate}
                    onSubtaskUpdate={onSubtaskUpdate}
                    onSubtaskDelete={onSubtaskDelete}
                    onStatusChange={onStatusChange}
                    droppableProvided={provided}
                  />
                )}
              </Droppable>
            </div>
          </div>
        </DragDropContext>
      </div>
    </ErrorBoundary>
  );
}
